import React from 'react'
import { useTranslation } from 'react-i18next'
import { msg, Trans } from '@lingui/macro'
import { MessageDescriptor } from '@lingui/core'
import { useLingui } from '@lingui/react'
import {
  Typography,
  CircularProgress,
  Stack,
  Box,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  LinearProgress,
  TablePagination,
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonIcon from '@mui/icons-material/Person'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import localizedFormat from 'dayjs/plugin/localizedFormat'

import { FormSubmission, FormSubmissionStatus, FormFieldType } from '@/core/models/form.model'

// Extend dayjs with plugins
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'

const getStatusColor = (status: FormSubmissionStatus) => {
  switch (status) {
    case FormSubmissionStatus.Submitted:
      return 'success'
    case FormSubmissionStatus.Draft:
      return 'warning'
    default:
      return 'default'
  }
}

const statusLabels: Partial<Record<FormSubmissionStatus, MessageDescriptor>> = {
  [FormSubmissionStatus.Draft]: msg`Draft`,
  [FormSubmissionStatus.Submitted]: msg`Submitted`,
}

export interface FormSubmissionsListProps {
  submissions?: FormSubmission[]
  isLoading: boolean
  error?: Error
  onEditDraft?: (submissionId: string) => void
  totalCount: number
  page: number
  onPageChange: (page: number) => void
  pageSize: number
  onPageSizeChange: (pageSize: number) => void
  onGeneratePdf?: (submissionId: string) => void
  generatingPdfId?: string | null
}

const formatValue = (value: any, type: string): string => {
  if (value === null || value === undefined) return '-'

  if (Array.isArray(value)) {
    return value.join(', ')
  }

  switch (type) {
    case FormFieldType.Boolean:
      return value ? 'Yes' : 'No'
    case FormFieldType.Number:
      return value.toString()
    case FormFieldType.Date:
      return dayjs(value).format('YYYY-MM-DD')
    case FormFieldType.DateTime:
      return dayjs(value).format('YYYY-MM-DD HH:mm')
    default:
      return value.toString()
  }
}

export default function FormSubmissionsList({
  submissions,
  isLoading,
  error,
  onEditDraft,
  totalCount,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  onGeneratePdf,
  generatingPdfId,
}: FormSubmissionsListProps) {
  const lingui = useLingui()
  const { t } = useTranslation()
  const [expandedItems, setExpandedItems] = React.useState<{ [key: string]: boolean }>({})

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const formatDateTime = (date: string) => {
    const d = dayjs(date)
    if (!d.isValid()) return '-'
    return d.format('lll') // e.g. "Jan 15, 2024 14:30"
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    onPageChange(newPage)
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPageSizeChange(parseInt(event.target.value, 10))
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {t('common.error.loadingFailed')}
      </Alert>
    )
  }

  if (isLoading && (!submissions || submissions.length === 0)) {
    return (
      <Stack alignItems="center" justifyContent="center" py={3}>
        <CircularProgress />
      </Stack>
    )
  }

  if (!submissions || submissions.length === 0) {
    return (
      <Typography color="text.secondary" align="center" py={4}>
        <Trans>No form submissions</Trans>
      </Typography>
    )
  }

  return (
    <Stack spacing={2}>
      {isLoading && <LinearProgress />}

      <Stack spacing={2}>
        {submissions.map((submission) => {
          const canGeneratePdf = submission.template.aiOutputConfig?.enabled

          return (
            <Paper
              key={submission._id}
              sx={{
                p: 3,
              }}
            >
              {/* Header with form name and status */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{submission.template.title}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={statusLabels[submission.status] ? lingui._(statusLabels[submission.status]!) : submission.status} color={getStatusColor(submission.status)} size="small" />
                  {canGeneratePdf && onGeneratePdf && (
                    <Tooltip title={t('page.forms.submission.generatePdf', 'Generate PDF')}>
                      <IconButton
                        size="small"
                        onClick={() => onGeneratePdf(submission._id)}
                        disabled={generatingPdfId === submission._id}
                      >
                        {generatingPdfId === submission._id ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                      </IconButton>
                    </Tooltip>
                  )}
                  {onEditDraft && (
                    <Tooltip title={t('page.forms.submission.actions.edit')}>
                      <IconButton size="small" onClick={() => onEditDraft(submission._id)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>

              <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography>
                    {submission.submittedBy?.userInfo.name} {submission.submittedBy?.userInfo.surname}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography>
                    {submission.status === FormSubmissionStatus.Submitted ? (
                      <Trans>Submitted at: {formatDateTime(submission.submittedAt!)}</Trans>
                    ) : (
                      <Trans>Last modified: {formatDateTime(submission.updatedAt)}</Trans>
                    )}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Add collapse toggle button */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  <Trans>Responses</Trans>
                </Typography>
                <IconButton onClick={() => toggleExpand(submission._id)}>{expandedItems[submission._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
              </Box>

              {/* Collapsible form content */}
              <Collapse in={expandedItems[submission._id]}>
                <Stack spacing={3}>
                  {submission.template.fields.map((field) => {
                    const response = submission.responses.find((r) => r.fieldId === field.id)
                    return (
                      <Box key={field.id}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {field.label}
                          {field.required && (
                            <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                              *
                            </Typography>
                          )}
                        </Typography>
                        <Typography>{formatValue(response?.value, field.type)}</Typography>
                      </Box>
                    )
                  })}
                </Stack>
              </Collapse>
            </Paper>
          )
        })}
      </Stack>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Stack>
  )
}
